import React, { useRef } from "react";
import { Product } from "./QuotationEdit";

interface QuotationPrintProps {
  quotationNumber: string;
  quotationSubject: string;
  quotationType: number;
  opportunity: { id: number; name: string } | null;
  validUntil: string;
  products: Product[];
  total: number;
  totalDiscount: number;
  totalGST: number;
  groupTotal: number;
  termsAndConditions: string;
  gstType: string | null;
  fromCompanyName?: string;
  fromAddress?: string;
  fromPhone?: string;
  fromEmail?: string;
  toClientName?: string;
  toAddress?: string;
  toEmail?: string;
}

const QuotationPrint: React.FC<QuotationPrintProps> = ({
  quotationNumber,
  quotationSubject,
  quotationType,
  opportunity,
  validUntil,
  products,
  total,
  totalDiscount,
  totalGST,
  groupTotal,
  termsAndConditions,
  gstType,
  fromCompanyName = "N/A",
  fromAddress = "N/A",
  fromPhone = "N/A",
  fromEmail = "N/A",
  toClientName = opportunity?.name || "N/A",
  toAddress = "N/A",
  toEmail = "N/A",
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handlePrint = () => {
    const isService = quotationType === 2;

    // Check if any product has a non-zero IGST
    const activeProducts = quotationType === 2
      ? products // For services, include all
      : products.filter(p => p.status === 1); // For products, only active ones


    const hasIGST =
      !isService && activeProducts.some((product) => (product.IGST || 0) > 0);

    // Calculate separate SGST, CGST, IGST totals for products
    const calculateProductTotals = () => {
      return activeProducts.map((product) => {
        const price = product.amount;
        const qty = isService ? 1 : product.qty || 1;
        const subtotal = price * qty;

        const discountValue = product.discount || 0;
        const discount =
          product.discountType === 1
            ? subtotal * (discountValue / 100)
            : discountValue;
        const taxableAmount = subtotal - discount;

        const sgstRate = product.SGST || 0;
        const cgstRate = product.CGST || 0;
        const igstRate = product.IGST || 0;
        const effectiveGstRate = isService ? product.effectiveGst || 0 : 0;

        const sgstAmount = !isService ? taxableAmount * (sgstRate / 100) : 0;
        const cgstAmount = !isService ? taxableAmount * (cgstRate / 100) : 0;
        const igstAmount = !isService ? taxableAmount * (igstRate / 100) : 0;
        const gstAmount = isService
          ? taxableAmount * (effectiveGstRate / 100)
          : 0;

        const total =
          taxableAmount +
          (isService ? gstAmount : sgstAmount + cgstAmount + igstAmount);

        return {
          subtotal,
          discount,
          sgstAmount,
          cgstAmount,
          igstAmount,
          gstAmount,
          total,
        };
      });
    };

    const aggregatedTotals = calculateProductTotals();
    const totalSGST = aggregatedTotals.reduce(
      (sum, p) => sum + p.sgstAmount,
      0
    );
    const totalCGST = aggregatedTotals.reduce(
      (sum, p) => sum + p.cgstAmount,
      0
    );
    const totalIGST = aggregatedTotals.reduce(
      (sum, p) => sum + p.igstAmount,
      0
    );
    const totalServiceGST = aggregatedTotals.reduce(
      (sum, p) => sum + p.gstAmount,
      0
    );

    const currentDate = new Date()
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .split("/")
      .join("-");

    const printContent = `
      <html>
        <head>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Lato&display=swap');
            body {
              font-family: 'Lato', sans-serif;
              margin: 0;
              padding: 0;
              background-color: #f5f5f5;
              color: #333333;
              line-height: 1.6;
            }
            .quotation-heading {
              border-bottom: 2px solid #f4a261;
              display: inline-block;
              padding-bottom: 3px;
            }
            .container {
              max-width: 900px;
              margin: 20px auto;
              background: white;
              border: 1px solid #e0e0e0;
              box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
            }
            .header {
              background-color: #1a3c34;
              color: white;
              padding: 20px 30px;
              text-align: center;
              border-bottom: 4px solid #f4a261;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .header h1 {
              margin: 0;
              font-size: 26px;
              font-weight: 700;
            }
            .header .date {
              font-size: 14px;
              font-weight: 400;
              opacity: 0.9;
            }
            .from-to {
              padding: 20px 30px;
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              background: #f9f9f9;
              border-bottom: 1px solid #e0e0e0;
            }
            .from-to h4 {
              font-size: 16px;
              font-weight: 600;
              margin-bottom: 10px;
              color: #1a3c34;
            }
            .from-to p {
              margin: 5px 0;
              font-size: 13px;
              color: #555555;
            }
            .from-to p span.label {
              color: #333333;
              font-weight: 600;
              text-transform: uppercase;
              margin-right: 5px;
            }
            .details {
              padding: 20px 30px;
              border-bottom: 1px solid #e0e0e0;
            }
            .details .row {
              display: grid;
              grid-template-columns: 1fr;
              gap: 10px;
              margin-bottom: 10px;
            }
            .details .row.two-columns {
              grid-template-columns: 1fr 1fr;
            }
            .details p {
              margin: 5px 0;
              font-size: 13px;
              color: #555555;
            }
            .details p strong {
              color: #333333;
              font-weight: 600;
            }
            .table-container {
              padding: 20px 30px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
              border: 1px solid #e0e0e0;
            }
            th, td {
              padding: 12px 15px;
              text-align: left;
              border-bottom: 1px solid #e0e0e0;
              vertical-align: top;
            }
th {
  background-color: #ffffff;
  color: #333333;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  border-bottom: 1px solid #e0e0e0;
}
            td {
              color: #555555;
            }
            tr:last-child td {
              border-bottom: none;
            }
            tr:nth-child(odd) {
              background-color: #fafafa;
            }
            .description {
              display: block;
              font-size: 12px;
              color: #333333;
              margin-top: 5px;
              font-style: italic;
              text-align: justify;
            }
            .description-label {
                background-color: #ffffff;
                color: #333333;
                font-weight: 600;
                font-size: 12px;
                text-transform: uppercase;
            }
            .product-group {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .totals {
              padding: 20px 30px;
              background: #f9f9f9;
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .totals p {
              margin: 8px 0;
              font-size: 13px;
              display: flex;
              justify-content: space-between;
              max-width: 300px;
              margin-left: auto;
              color: #333333;
            }
            .totals p strong {
              font-weight: 600;
            }
            .totals p.grand-total {
              font-weight: 700;
              font-size: 15px;
              color: #333333;
              border-top: 1px solid #e0e0e0;
              padding-top: 8px;
            }
            .total-column {
              text-align: right;
            }
            .terms {
              padding: 20px 30px;
              background: white;
              font-size: 13px;
              page-break-before: always;
            }
            .terms h4 {
              margin: 0 0 12px;
              font-size: 16px;
              color: #1a3c34;
              font-weight: 600;
              border-bottom: 2px solid #f4a261;
              display: inline-block;
              padding-bottom: 3px;
            }
            .terms p {
              color: #555555;
              margin: 0;
              white-space: pre-wrap;
            }
            .footer {
              background: #1a3c34;
              color: #ffffff;
              padding: 15px 30px;
              text-align: center;
              font-size: 12px;
              border-top: 2px solid #f4a261;
            }
            @media print {
            @page {
              margin: 0;
              size: auto;
            }

            body {
              margin: 0;
              padding: 0;
              background: white;
            }
            .container {
              margin: 0;
              width: 100%;
              box-shadow: none;
              border: none;
            }
            .terms {
              page-break-before: always;
              text-align: justify;
            }
            .footer {
              position: fixed;
              bottom: 0;
              width: 100%;
              background: #1a3c34;
            }
            .product-group {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .description-row {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .no-print-bg {
              background: none !important;
              color: black !important;
              border: none !important;
            }
          }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header no-print-bg">
              <h1 class="quotation-heading">Quotation #${quotationNumber}</h1>
              <span class="date"><strong>Invoice Date:</strong> ${currentDate}</span>
            </div>
            <div class="from-to">
              <div>
                <h4>From</h4>
                <p><span class="label">Company Name:</span> ${fromCompanyName}</p>
                <p><span class="label">Phone:</span> ${fromPhone}</p>
                <p><span class="label">Email:</span> ${fromEmail}</p>
                <p><span class="label">Address:</span> ${fromAddress}</p>
              </div>
              <div>
                <h4>To</h4>
                <p><span class="label">Client Name:</span> ${toClientName}</p>
                <p><span class="label">Email:</span> ${toEmail}</p>
                <p><span class="label">Address:</span> ${toAddress}</p>
              </div>
            </div>
            <div class="details">
              <div class="row two-columns">
                <p><strong>Type:</strong> ${isService ? "Service" : "Product"
      }</p>
                <p><strong>Valid Till:</strong> ${validUntil}</p>
              </div>
              <div class="row">
                <p><strong>Subject:</strong> ${quotationSubject}</p>
              </div>
            </div>
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th>${isService ? "Service" : "Product"} Name</th>
                    <th>Price</th>
                    ${isService ? "" : "<th>Quantity</th>"}
                    <th>Discount</th>
                    ${isService
        ? `<th>GST</th>`
        : `
                          <th>SGST</th>
                          <th>CGST</th>
                          ${hasIGST ? "<th>IGST</th>" : ""}
                        `
      }
                   <th class="total-column">Total</th>
                  </tr>
                </thead>
                <tbody>
${activeProducts.map((product) => {

        const price = product.amount;
        const qty = isService ? 1 : product.qty || 1;
        const subtotal = price * qty;
        const discountValue = product.discount || 0;
        const discount =
          product.discountType === 1
            ? subtotal * (discountValue / 100)
            : discountValue;
        const taxableAmount = subtotal - discount;
        const sgstRate = product.SGST || 0;
        const cgstRate = product.CGST || 0;
        const igstRate = product.IGST || 0;
        const effectiveGstRate = isService
          ? product.effectiveGst || 0
          : 0;
        const sgstAmount = !isService
          ? taxableAmount * (sgstRate / 100)
          : 0;
        const cgstAmount = !isService
          ? taxableAmount * (cgstRate / 100)
          : 0;
        const igstAmount = !isService
          ? taxableAmount * (igstRate / 100)
          : 0;
        const gstAmount = isService
          ? taxableAmount * (effectiveGstRate / 100)
          : 0;
        const total =
          taxableAmount +
          (isService
            ? gstAmount
            : sgstAmount + cgstAmount + igstAmount);

        return `
                        <tr class="product-group">
                          <td>${product.name}</td>
                          <td>₹${product.amount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}</td>
                          ${isService ? "" : `<td>${product.qty || 1}</td>`}
                          <td>
                          ${product.discount
            ? `${product.discountType === 1
              ? `${product.discount}%`
              : `₹${product.discount.toLocaleString(
                "en-IN",
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}`
            }`
            : "N/A"
          }
                        </td>

                          ${isService
            ? `<td>${product.effectiveGst}%</td>`
            : `
                                <td>${sgstRate}%</td>
                                <td>${cgstRate}%</td>
                                ${hasIGST ? `<td>${igstRate}%</td>` : ""}
                              `
          }
                          <td class="total-column">₹${total.toLocaleString(
            "en-IN",
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}</td>
                        </tr>
                        ${product.description
            ? `
                              <tr class="product-group description-row">
                                <td colspan="${isService ? 5 : hasIGST ? 8 : 7
            }" style="font-size: 12px; color: #555; padding-left: 10px;">
                                  <span class="description-label">DESCRIPTION:</span> ${product.description
            }
                                </td>
                              </tr>
                            `
            : ""
          }
                      `;
      })
        .join("")}
                </tbody>
              </table>
            </div>
           <div class="totals">
  <p><strong>Subtotal:</strong> ₹${total.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}</p>
  <p><strong>Discount:</strong> ₹${totalDiscount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}</p>
  ${isService
        ? `<p><strong>GST:</strong> ₹${totalServiceGST.toLocaleString(
          "en-IN",
          { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        )}</p>`
        : `
        <p><strong>SGST:</strong> ₹${totalSGST.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}</p>
        <p><strong>CGST:</strong> ₹${totalCGST.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}</p>
        ${hasIGST
          ? `<p><strong>Total IGST:</strong> ₹${totalIGST.toLocaleString(
            "en-IN",
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          )}</p>`
          : ""
        }
      `
      }
  <p class="grand-total"><strong>Grand Total:</strong> ₹${groupTotal.toLocaleString(
        "en-IN",
        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      )}</p>
</div>

           ${termsAndConditions
        ? `
        <div class="terms">
          <h4>Terms and Conditions</h4>
          <p>${termsAndConditions.replace(/\n/g, "<br>")}</p>
        </div>
      `
        : `
        <div class="terms">
          <h4>Terms and Conditions</h4>
          <p>There are no terms and conditions for this ${quotationType === 2 ? "service" : "product"
        }.</p>
        </div>
      `
      }
            <div class="footer no-print-bg">
              <p>Generated by ${fromCompanyName} | Contact: ${fromEmail}</p>
            </div>
          </div>
        </body>
      </html>
    `;

    const iframe = iframeRef.current;
    if (iframe) {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(printContent);
        doc.close();
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      }
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handlePrint}
        className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm w-full sm:w-auto disabled:opacity-50 relative bg-gray-500 hover:bg-gray-600 text-white rounded-lg overflow-hidden transition-all duration-300"
      >
        <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></span>
        <span className="relative flex justify-center items-center gap-2">Print</span>
      </button>
      <iframe ref={iframeRef} style={{ display: "none" }} title="print-frame" />
    </>
  );
};

export default QuotationPrint;
